/**
 * Executive report, the calm version (DEC-190 decisions 1 to 6, UI_SDD §R.4).
 *
 * Top to bottom: the decision sentence as the title, with "See the fix plan" as the main
 * action beside it; one band of four numbers and the estimate line under it; then "What
 * to do now" (three actions, any critical one first) beside "More detail", four closed
 * sections of which "If you wait a year" opens by default and only one is open at a time.
 *
 * Every figure is a shipped field (DEC-059, DEC-060); none is computed here except the
 * quarter bars, which use the backend's own 10%-a-quarter model. The words and the order
 * live in `components/executive/reportModel.ts` so they can be tested on their own.
 */
import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowRight, ChevronRight, Minus, Plus, RefreshCw } from 'lucide-react'
import { getExecutiveReport } from '../api/executive'
import { scansApi } from '../api/scans'
import { ScanModeBanner } from '../components/ScanModeBanner'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/shared/Button'
import { Card } from '../components/shared/Card'
import { SeverityBadge } from '../components/shared/Badge'
import { PageTitle } from '../components/shared/PageTitle'
import { Spinner } from '../components/shared/Spinner'
import { Pill } from '../components/trends/Pill'
import { firstSentence } from '../components/trends/trendMath'
import {
  DEFAULT_OPEN_SECTION,
  SEVERITY_LABEL,
  bandCells,
  buildLedger,
  decisionParts,
  detailHint,
  hoursLabel,
  impactLine,
  plainTitle,
  toggleSection,
  waitingQuarters,
  type DetailSection,
} from '../components/executive/reportModel'
import { useProfile } from '../hooks/useAuth'
import type {
  ExecutiveMetrics,
  ScanReportResponse,
  ScanStatusResponse,
  Severity,
  TopFinding,
} from '../types/api'
import { dateTimeLabel, dollars, isUploadScan, repoLabel } from '../utils/format'
import { ranHeader } from '../utils/runTime'
import { filesLabel, sourceManifestSentence } from '../utils/folderUpload'
import { SETTINGS_PATH } from '../components/settings/settingsSections'

// ---------------------------------------------------------------------------
// Who can open what (mirrors App.tsx's RequireProfile; the backend is the real gate)
// ---------------------------------------------------------------------------

/** The Findings route is gated to `engineering`; admin and operator tokens pass every gate. */
function canOpenFindings(profile: string | null): boolean {
  return profile === null || profile === 'admin' || profile === 'engineering'
}

const NO_FIX_PLAN_ACCESS =
  'The fix plan is in the Engineering view, which the Engineering and Admin profiles can open. Ask your workspace admin for access.'

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

const BandLabel = ({ children }: { children: ReactNode }) => (
  <span className="text-[11px] font-semibold leading-none tracking-[3px] uppercase text-muted">
    {children}
  </span>
)

const CardTitle = ({ bar, children }: { bar: string; children: ReactNode }) => (
  <div className="flex items-center gap-3">
    <span className={`w-1 h-[22px] rounded-[2px] flex-none ${bar}`} />
    <h2 className="text-[14px] font-semibold leading-none tracking-[0.16em] uppercase text-ink m-0">
      {children}
    </h2>
  </div>
)

function FixPlanButton({
  canOpen,
  size,
  onOpen,
  testId,
}: {
  canOpen: boolean
  size: 'md' | 'lg'
  onOpen: () => void
  testId: string
}) {
  return (
    <span data-testid={testId} className="contents">
      <Button size={size} onClick={onOpen} disabled={!canOpen}>
        See the fix plan <ArrowRight className={size === 'lg' ? 'w-[18px] h-[18px]' : 'w-4 h-4'} />
      </Button>
    </span>
  )
}

/** One open-or-closed section of "More detail". */
function DetailRow({
  id,
  title,
  summary,
  open,
  onToggle,
  children,
}: {
  id: DetailSection
  title: string
  summary: ReactNode
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <div className="border-t border-hairline" data-testid={`detail-${id}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`detail-${id}-body`}
        className="w-full flex items-center justify-between gap-3 text-left py-[14px] cursor-pointer"
      >
        <span className="flex flex-col gap-[3px] min-w-0">
          <span className="text-[14px] font-semibold text-ink">{title}</span>
          <span className="text-[13px] text-muted">{summary}</span>
        </span>
        <ChevronRight
          className={`w-4 h-4 text-muted flex-none transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div id={`detail-${id}-body`} className="pb-[18px]">
          {children}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The AI summary line (decision 5)
// ---------------------------------------------------------------------------

function SummaryLine({
  metrics,
  isRegenerating,
  onRegenerate,
  canOpenSettings,
  onOpenSettings,
}: {
  metrics: ExecutiveMetrics
  isRegenerating: boolean
  onRegenerate: () => void
  canOpenSettings: boolean
  onOpenSettings: () => void
}) {
  const [open, setOpen] = useState(false)
  const narrative = metrics.executive_narrative
  const error = metrics.narrative_error
  // A backend that predates the code sends only the text; that is not a known
  // temporary failure, so it gets no "Try again".
  const code = metrics.narrative_error_code ?? (error ? 'other' : null)

  if (isRegenerating || (narrative === null && error === null)) {
    return (
      <div className="flex flex-col gap-2 max-w-[640px]" data-testid="summary-loading" aria-live="polite">
        <div className="h-[14px] bg-fill-subtle rounded animate-pulse w-full" />
        <div className="h-[14px] bg-fill-subtle rounded animate-pulse w-[70%]" />
        <span className="text-[12px] text-muted">
          {isRegenerating ? 'Writing the summary again.' : 'Writing a short summary of this report.'}
        </span>
      </div>
    )
  }

  if (narrative) {
    const first = firstSentence(narrative) ?? narrative
    const rest = narrative.trim().slice(first.length).trim()
    return (
      <div className="flex flex-col gap-2 pl-[14px] border-l-2 border-accent" data-testid="summary">
        <p className="text-[15px] leading-[1.55] text-body m-0">{first}</p>
        {open && rest && (
          <p className="text-[14px] leading-[1.6] text-body m-0 whitespace-pre-wrap">{rest}</p>
        )}
        <div className="flex flex-wrap items-center gap-x-[14px] gap-y-1 text-[12px] text-muted">
          <span>Written by AI from your scan data. The figures are computed, not generated.</span>
          {rest && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-accent-text font-semibold hover:underline"
            >
              {open ? 'Show less' : 'Read the full summary'}
            </button>
          )}
          {(open || !rest) && (
            <button
              type="button"
              onClick={onRegenerate}
              className="inline-flex items-center gap-[5px] text-body font-semibold hover:text-ink"
            >
              <RefreshCw className="w-[11px] h-[11px]" /> Regenerate
            </button>
          )}
        </div>
      </div>
    )
  }

  if (code === 'no_key') {
    return (
      <p className="text-[14px] leading-[1.5] text-body m-0" data-testid="summary-no-key">
        No written summary: no AI key is set for this workspace. The figures do not need it.{' '}
        {canOpenSettings ? (
          <button
            type="button"
            onClick={onOpenSettings}
            className="text-accent-text font-semibold hover:underline"
          >
            Add an AI key in Settings
          </button>
        ) : (
          <span className="text-accent-text font-semibold">Ask an admin to add an AI key in Settings</span>
        )}
      </p>
    )
  }

  return (
    <p className="text-[14px] leading-[1.5] text-body m-0" data-testid="summary-failed">
      {code === 'temporary'
        ? 'The written summary could not be produced just now. The figures do not need it.'
        : 'The written summary could not be produced. The figures do not need it.'}
      {code === 'temporary' && (
        <>
          {' '}
          <button
            type="button"
            onClick={onRegenerate}
            className="text-accent-text font-semibold hover:underline"
          >
            Try again
          </button>
        </>
      )}
    </p>
  )
}

// ---------------------------------------------------------------------------
// Scan facts
// ---------------------------------------------------------------------------

function sourceLine(scan: ScanStatusResponse | undefined, report: ScanReportResponse | undefined): string | null {
  const url = scan?.repo_url ?? report?.repo_url
  if (!url) return null
  const parts: string[] = []
  const manifest = scan?.source_manifest
  if (manifest && manifest.kind === 'upload') {
    parts.push('Uploaded folder', filesLabel(manifest.file_count))
  } else if (isUploadScan(url)) {
    parts.push(`Uploaded folder ${repoLabel(url)}`)
  } else if (url.startsWith('local://')) {
    parts.push(`Server folder ${repoLabel(url)}`)
  } else {
    parts.push(repoLabel(url))
    const branch = scan?.branch ?? report?.branch
    if (branch) parts.push(`branch ${branch}`)
  }
  const started = scan?.started_at ?? report?.started_at
  if (started) parts.push(`scanned ${dateTimeLabel(started).replace(' · ', ' at ')}`)
  // DEC-195: the run time, without waits for a person.
  const ran = ranHeader(scan ?? report)
  if (ran) parts.push(ran.charAt(0).toLowerCase() + ran.slice(1))
  return parts.join(' · ')
}

function aiSentence(mode: ScanStatusResponse['llm_mode'] | undefined): string | null {
  switch (mode) {
    case 'full':
      return 'AI checked the findings.'
    case 'not_configured':
    case 'static_by_choice':
      return "Scanned without AI: findings come from TDIA's built-in checks."
    case 'degraded_credits':
    case 'degraded_auth':
      return "AI stopped part way through: later findings come from TDIA's built-in checks."
    default:
      return null
  }
}

function debtIndexText(di: NonNullable<ScanReportResponse['debt_index']>): string {
  const per = di.denominator_basis === 'kloc' ? 'per 1,000 lines of code' : 'per 1,000 files'
  const density = `${di.debt_density.toFixed(1)} debt points ${per}.`
  let lead: string
  if (di.is_baseline || di.debt_index == null) {
    lead = 'This is the first scan, so it sets the starting point of 100. Later scans show whether debt went up or down.'
  } else if (di.debt_index >= 105) {
    lead = 'Higher than the first scan (100), so debt has grown since.'
  } else if (di.debt_index <= 95) {
    lead = 'Lower than the first scan (100), so debt has shrunk since.'
  } else {
    lead = 'About the same as the first scan (100).'
  }
  const caveat = di.comparability === 'degraded' && di.comparability_reason ? ` ${di.comparability_reason}` : ''
  return `${lead} ${density}${caveat}`
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ExecutiveReportPage() {
  const { scanId } = useParams<{ scanId: string }>()
  const navigate = useNavigate()
  const profile = useProfile()
  const [teamSize, setTeamSize] = useState(5)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [openSection, setOpenSection] = useState<DetailSection | null>(DEFAULT_OPEN_SECTION)
  const [openDetail, setOpenDetail] = useState<Record<string, boolean>>({})

  const { data: scan } = useQuery({
    queryKey: ['scan', scanId],
    queryFn: () => scansApi.get(scanId!),
    enabled: !!scanId,
  })

  const scanReady = !scan || scan.state === 'complete' || scan.state === 'partial'

  const { data: metrics, error, refetch } = useQuery<ExecutiveMetrics>({
    queryKey: ['executive-report', scanId, teamSize],
    queryFn: () => getExecutiveReport(scanId!, teamSize, true, false),
    enabled: !!scanId && scanReady,
    // Keep the page on screen while the team stepper recalculates.
    placeholderData: (prev) => prev,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      if (data.executive_narrative === null && data.narrative_error === null) return 3000
      return false
    },
  })

  const { data: report } = useQuery({
    queryKey: ['report-top', scanId],
    queryFn: () => scansApi.report(scanId!),
    enabled: !!scanId && scanReady,
  })

  const regenerateMutation = useMutation({
    mutationFn: () => getExecutiveReport(scanId!, teamSize, true, true),
    onMutate: () => setIsRegenerating(true),
    onSettled: () => {
      setIsRegenerating(false)
      refetch()
    },
  })

  // Mid-scan waiting state (§R.4): a running scan is not an error.
  if (scan && !scanReady) {
    return (
      <AppShell scanId={scanId} crumb="Executive report" active="report">
        <div className="h-full flex flex-col items-center justify-center gap-3">
          <Spinner />
          <p className="text-[13px] text-body">
            The scan is still running. The executive report unlocks when it completes.
          </p>
          <Button variant="secondary" onClick={() => navigate(`/scans/${scanId}/monitor`)}>
            Watch the monitor
          </Button>
        </div>
      </AppShell>
    )
  }

  const fixPlanAllowed = canOpenFindings(profile)
  const canOpenSettings = profile === null || profile === 'admin'
  const openFixPlan = () => {
    if (fixPlanAllowed) navigate(`/scans/${scanId}/findings`)
  }

  const top = report?.top_findings ?? []
  const ledger = buildLedger(top)
  const bySeverity = report?.summary.findings_by_severity ?? {}
  const count = (s: Severity) => bySeverity[s] ?? 0
  const totalFindings = report?.summary.total_findings ?? metrics?.total_findings ?? 0
  const meta = sourceLine(scan, report)
  // The no-key notice is the page's one mention of a missing key (decision 5), so the
  // scan-mode banner stays for the modes that say something else: AI that stopped
  // part way, or a scan waiting for a decision.
  const showModeBanner =
    !!scan?.llm_mode && !['full', 'not_configured', 'static_by_choice'].includes(scan.llm_mode)

  return (
    <AppShell scanId={scanId} crumb="Executive report" active="report">
      <div className="px-4 sm:px-7 py-[26px] max-w-[1180px] flex flex-col gap-5">
        {showModeBanner && (
          <ScanModeBanner
            llmMode={scan?.llm_mode}
            reason={scan?.llm_degraded_reason}
            fromAgent={scan?.llm_degraded_from_agent}
          />
        )}

        {error ? (
          <Card className="p-6 text-center text-[13px] text-body">
            Could not load the executive report. Try refreshing the page.
          </Card>
        ) : !metrics ? (
          <div className="py-24 flex justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            {/* ══ 1. The answer, and the main action ══ */}
            <div className="flex flex-wrap items-start justify-between gap-x-7 gap-y-4" data-testid="first-view">
              <div className="flex flex-col gap-3 min-w-0 max-w-[820px]">
                <PageTitle eyebrow="Executive report">
                  <DecisionTitle metrics={metrics} />
                </PageTitle>
                <SummaryLine
                  metrics={metrics}
                  isRegenerating={isRegenerating}
                  onRegenerate={() => regenerateMutation.mutate()}
                  canOpenSettings={canOpenSettings}
                  onOpenSettings={() => navigate(SETTINGS_PATH.general)}
                />
                {meta && <span className="text-[13px] text-muted" data-testid="scan-line">{meta}</span>}
              </div>
              <div className="flex flex-col items-start lg:items-end gap-2 flex-none lg:pt-[26px] max-w-[340px]">
                <FixPlanButton canOpen={fixPlanAllowed} size="lg" onOpen={openFixPlan} testId="fix-plan-top" />
                <span className="text-[12px] leading-[1.5] text-faint lg:text-right" data-testid="fix-plan-note">
                  {fixPlanAllowed ? (
                    'The full list of issues, by priority · PDF coming soon'
                  ) : (
                    <>
                      {NO_FIX_PLAN_ACCESS}
                      <br />
                      PDF coming soon
                    </>
                  )}
                </span>
              </div>
            </div>

            {/* ══ 2. Four numbers ══ */}
            <div className="flex flex-col gap-[10px]">
              <div
                data-testid="kpi-band"
                className="rounded-lg border border-hairline shadow-card px-[30px] py-3 lg:py-[26px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
                style={{
                  background:
                    'radial-gradient(120% 160% at 100% 100%, rgb(var(--accent) / 0.20), rgb(var(--panel)) 60%)',
                }}
              >
                {bandCells(metrics, count('critical')).map((b, i, all) => (
                  <div
                    key={b.label}
                    data-testid="band-cell"
                    className={`flex flex-col gap-3 min-w-0 py-4 lg:py-0 lg:px-[26px] lg:first:pl-0 lg:last:pr-0 ${
                      i < all.length - 1 ? 'border-b lg:border-b-0 lg:border-r border-hairline' : ''
                    }`}
                  >
                    <BandLabel>{b.label}</BandLabel>
                    <span className="tabular-nums text-[40px] leading-none font-medium text-ink whitespace-nowrap">
                      {b.value}
                      {b.suffix && <span className="text-[16px] text-muted">{b.suffix}</span>}
                    </span>
                    <span className="flex flex-wrap gap-[6px]">
                      <Pill tone={b.tone}>{b.pill}</Pill>
                    </span>
                    <span className="text-[13px] text-muted">{b.sub}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted px-1" data-testid="estimate-line">
                <span>Estimates for a team of</span>
                <span className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    aria-label="Fewer engineers"
                    onClick={() => setTeamSize((n) => Math.max(1, n - 1))}
                    className="w-6 h-6 rounded-sm border border-hairline-strong bg-panel text-body inline-flex items-center justify-center hover:text-ink"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="tabular-nums min-w-[26px] text-center text-[14px] font-medium text-ink" data-testid="team-size">
                    {teamSize}
                  </span>
                  <button
                    type="button"
                    aria-label="More engineers"
                    onClick={() => setTeamSize((n) => Math.min(10000, n + 1))}
                    className="w-6 h-6 rounded-sm border border-hairline-strong bg-panel text-body inline-flex items-center justify-center hover:text-ink"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </span>
                <span>
                  engineers at ${Math.round(metrics.hourly_rate).toLocaleString('en-US')} an hour. Not audited figures.
                </span>
                <button
                  type="button"
                  onClick={() => setOpenSection('method')}
                  className="text-accent-text font-semibold hover:underline"
                >
                  How this is calculated
                </button>
              </div>
            </div>

            {/* ══ 3. Actions, and the detail one click away ══ */}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)] gap-5 items-start">
              <Card className="px-[26px] pt-[22px] pb-[10px] flex flex-col">
                <CardTitle bar="bg-accent">What to do now</CardTitle>
                {ledger.length === 0 ? (
                  <p className="text-[14px] text-muted py-[18px] m-0">No findings, nothing to prioritize.</p>
                ) : (
                  <ol className="list-none m-0 p-0" data-testid="ledger">
                    {ledger.map((f, i) => (
                      <LedgerRow
                        key={f.finding_id}
                        n={i + 1}
                        finding={f}
                        last={i === ledger.length - 1}
                        open={!!openDetail[f.finding_id]}
                        onToggle={() => setOpenDetail((o) => ({ ...o, [f.finding_id]: !o[f.finding_id] }))}
                      />
                    ))}
                  </ol>
                )}
                <div className="flex flex-col gap-2 mt-[6px] mb-[14px] sm:ml-12">
                  <div className="self-start">
                    <FixPlanButton canOpen={fixPlanAllowed} size="md" onOpen={openFixPlan} testId="fix-plan-bottom" />
                  </div>
                  {!fixPlanAllowed && <span className="text-[12px] leading-[1.5] text-faint">{NO_FIX_PLAN_ACCESS}</span>}
                </div>
              </Card>

              <Card className="px-[22px] pt-5 pb-[6px] flex flex-col">
                <CardTitle bar="bg-accent">More detail</CardTitle>
                <span className="text-[13px] text-muted mt-2 mb-[6px] ml-4" data-testid="detail-hint">
                  {detailHint(openSection)}
                </span>

                <DetailRow
                  id="wait"
                  title="If you wait a year"
                  summary={<>The same fix costs {dollars(metrics.cost_of_inaction_1yr)} more. A modeled scenario.</>}
                  open={openSection === 'wait'}
                  onToggle={() => setOpenSection((o) => toggleSection(o, 'wait'))}
                >
                  <WaitingChart totalDebt={metrics.total_debt_cost_dollars} />
                </DetailRow>

                <DetailRow
                  id="severe"
                  title="Most severe issues"
                  summary={`${count('critical')} critical and ${count('high')} high, of ${totalFindings.toLocaleString('en-US')} issues.`}
                  open={openSection === 'severe'}
                  onToggle={() => setOpenSection((o) => toggleSection(o, 'severe'))}
                >
                  <div className="flex flex-col gap-1">
                    <span className="flex flex-wrap gap-[6px] pb-2">
                      {(['critical', 'high', 'medium', 'low'] as Severity[]).map((s) => (
                        <SeverityBadge key={s} severity={s}>
                          {count(s).toLocaleString('en-US')} {SEVERITY_LABEL[s]}
                        </SeverityBadge>
                      ))}
                    </span>
                    {top.slice(0, 3).map((f) => (
                      // A fixed pill column (DEC-193 decision 3): every title starts at the
                      // same place, and the summary sits under the title, not the pill.
                      <div
                        key={f.finding_id}
                        className="grid grid-cols-[92px_minmax(0,1fr)] gap-x-2 gap-y-[5px] items-start py-[10px] border-t border-hairline"
                      >
                        <SeverityBadge severity={f.severity} />
                        <span className="text-[14px] font-semibold text-ink">{plainTitle(f)}</span>
                        <span className="col-start-2 text-[13px] leading-[1.5] text-muted">
                          {f.human_summary} <span className="text-[13px]">{whereLabel(f)}</span>
                        </span>
                      </div>
                    ))}
                    {fixPlanAllowed ? (
                      <button
                        type="button"
                        onClick={openFixPlan}
                        className="self-start mt-[6px] text-[13px] font-semibold text-accent-text hover:underline"
                      >
                        See all {totalFindings.toLocaleString('en-US')} in the Engineering view
                      </button>
                    ) : (
                      <span className="mt-[6px] text-[12px] leading-[1.5] text-faint">{NO_FIX_PLAN_ACCESS}</span>
                    )}
                  </div>
                </DetailRow>

                <DetailRow
                  id="figures"
                  title="More figures and what was scanned"
                  summary="Debt weighted by confidence, Debt Index, the files read."
                  open={openSection === 'figures'}
                  onToggle={() => setOpenSection((o) => toggleSection(o, 'figures'))}
                >
                  <FiguresList metrics={metrics} report={report} scan={scan} />
                </DetailRow>

                <DetailRow
                  id="method"
                  title="How this is calculated"
                  summary="Formulas, team size, hourly rate, sources."
                  open={openSection === 'method'}
                  onToggle={() => setOpenSection((o) => toggleSection(o, 'method'))}
                >
                  <div className="flex flex-col gap-[10px] text-[12.5px] leading-[1.6] text-body">
                    <p className="m-0">{metrics.methodology_disclosure}</p>
                    <p className="m-0 text-muted">{metrics.team_size_disclosure}</p>
                    <p className="m-0 text-muted">
                      Hourly rate: ${Math.round(metrics.hourly_rate).toLocaleString('en-US')}, set for this workspace.
                    </p>
                    <p className="m-0 text-muted">Sources: {metrics.benchmark_sources.join(' ')}</p>
                  </div>
                </DetailRow>
              </Card>
            </div>
          </>
        )}
      </div>
    </AppShell>
  )
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/** The title: the fixed template with its numbers in mono (decision 1). */
function DecisionTitle({ metrics }: { metrics: ExecutiveMetrics }) {
  const p = decisionParts(metrics)
  // Tabular Work Sans, not monospace (DEC-193 decision 4).
  const mono = (s: string) => <span className="tabular-nums font-medium">{s}</span>
  if (p.fix === null) return <>This scan found nothing that needs fixing.</>
  if (p.months === null)
    return <>A one-time fix of {mono(p.fix)}. TDIA measured no slowdown for it to pay back.</>
  return (
    <>
      A one-time fix of {mono(p.fix)} pays for itself in {mono(p.months)} months.
    </>
  )
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function whereLabel(f: TopFinding): string {
  return f.line_start != null ? `${f.file}:${f.line_start}` : f.file
}

function LedgerRow({
  n,
  finding: f,
  last,
  open,
  onToggle,
}: {
  n: number
  finding: TopFinding
  last: boolean
  open: boolean
  onToggle: () => void
}) {
  return (
    <li className={`flex gap-4 py-[18px] ${last ? '' : 'border-b border-hairline'}`} data-testid="ledger-row">
      <span className="tabular-nums w-8 h-8 rounded-full bg-accent text-white text-[15px] font-semibold inline-flex items-center justify-center flex-none">
        {n}
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-[6px]">
        <span className="text-[16px] font-semibold leading-[1.35] text-ink" data-testid="ledger-title">
          {plainTitle(f)}
        </span>
        <span className="text-[14px] text-body">{impactLine(f.category)}</span>
        {/* Fixed pill slots (DEC-193 decision 3): severity, then hours, so the pills
            and the link after them start at the same place on every row. */}
        <span className="grid grid-cols-[92px_136px_auto] items-center justify-start gap-2 mt-[2px]">
          <SeverityBadge severity={f.severity} />
          <Pill tone="neutral">{hoursLabel(f.estimated_effort_hours)}</Pill>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="inline-flex items-center gap-1 pl-1 text-[12px] font-semibold text-muted hover:text-ink"
          >
            <ChevronRight className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`} />
            Technical detail
          </button>
        </span>
        {open && (
          <span className="flex flex-col gap-1 mt-1 px-3 py-[10px] rounded-md bg-fill-subtle text-[12.5px] leading-[1.5] text-body">
            <span>{f.human_summary}</span>
            <span className="text-[13px] text-muted">{whereLabel(f)}</span>
          </span>
        )}
      </div>
    </li>
  )
}

/** Extra cost of the same fix by quarter: one accent hue, never severity colours (decision 6). */
function WaitingChart({ totalDebt }: { totalDebt: number }) {
  const quarters = waitingQuarters(totalDebt)
  const maxQ = Math.max(1, ...quarters)
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-[10px] h-[110px]" data-testid="waiting-chart">
        {quarters.map((v, i) => (
          <div key={i} className="flex-1 h-full flex flex-col justify-end items-center gap-[6px]">
            <span className="tabular-nums text-[11px] text-body">{dollars(v)}</span>
            {/* The bar's height is a share of its own track, not of the column, so the labels
                above and below cannot squash the tallest bars to the same height. */}
            <span className="flex-1 w-full flex items-end min-h-0">
              <span
                className="block w-full rounded-t-sm bg-accent"
                data-testid="waiting-bar"
                style={{ height: `${Math.max(8, (v / maxQ) * 100)}%`, opacity: i === 3 ? 1 : 0.35 + i * 0.15 }}
              />
            </span>
            <span className="text-[11px] text-muted">After Q{i + 1}</span>
          </div>
        ))}
      </div>
      <p className="text-[13px] leading-[1.5] text-body m-0">
        Extra cost to fix the same issues, by quarter. It assumes the fix gets 10% dearer each quarter
        as new code builds on top. TDIA has not yet checked that rate against real projects.
      </p>
    </div>
  )
}

function FiguresList({
  metrics,
  report,
  scan,
}: {
  metrics: ExecutiveMetrics
  report: ScanReportResponse | undefined
  scan: ScanStatusResponse | undefined
}) {
  const rows: { k: string; v: string; t: string }[] = [
    {
      k: 'Debt weighted by confidence',
      v: dollars(metrics.expected_debt_cost_dollars),
      t: 'The fix cost, with each issue counted by how sure TDIA is that it is real. Lower than the fix cost because some issues are less certain.',
    },
  ]
  const di = report?.debt_index
  if (di) {
    rows.push({
      k: 'Debt Index',
      v: di.debt_index == null ? 'n/a' : String(Math.round(di.debt_index)),
      t: debtIndexText(di),
    })
  }
  rows.push({
    k: 'Issues found',
    v: metrics.total_findings.toLocaleString('en-US'),
    t: `${capitalize(hoursLabel(metrics.total_effort_hours))} of work to fix them all.`,
  })
  const url = scan?.repo_url ?? report?.repo_url
  if (url) {
    const manifest = scan?.source_manifest
    const isUpload = manifest && manifest.kind === 'upload'
    const what = isUpload
      ? sourceManifestSentence(manifest)
      : `${repoLabel(url)}${(scan?.branch ?? report?.branch) ? `, branch ${scan?.branch ?? report?.branch}` : ''}`
    const ai = aiSentence(scan?.llm_mode)
    rows.push({
      k: 'What was scanned',
      v: isUpload ? filesLabel(manifest.file_count) : repoLabel(url),
      t: [`${what}.`, ai].filter(Boolean).join(' '),
    })
  }
  return (
    <div className="flex flex-col" data-testid="figures">
      {rows.map((r) => (
        <div key={r.k} className="flex flex-col gap-1 py-[10px] border-t border-hairline first:border-t-0">
          <span className="flex justify-between items-baseline gap-3">
            <span className="text-[13px] font-semibold text-body">{r.k}</span>
            <span className="tabular-nums text-[16px] font-medium text-ink whitespace-nowrap">{r.v}</span>
          </span>
          <span className="text-[12.5px] leading-[1.5] text-muted">{r.t}</span>
        </div>
      ))}
    </div>
  )
}
